import { useState, useEffect, useMemo } from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Loading, SummaryCardNew } from '@hapvida/hapvida-core-components';

import FinancialService from '@services/FinancialService';

import { toastifyApiErrors } from '@utils';

import type { CopartReportCardProps } from './types';
import { ReportButton } from './styles';
import { ReportButtonContent } from './components/ReportButtonContent';
import { handleDownloadReport } from './utils/handleDownloadReport';
import { useBillState } from '../../hooks';
import { ErrorOrNotFoundContent } from './components/ErrorOrNotFoundContent';
import { AlertReport } from '../AlertReport';

export function CopartReportCard({
  reports,
  personNumber,
  remittenceCode,
}: Readonly<CopartReportCardProps>) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const { coparticipationReportRequestState } = useBillState();
  const service = new FinancialService();

  const [loading, setLoading] = useState(() => {
    const storageKey = `${personNumber}_${remittenceCode}_loadingState`;
    const loadingState = localStorage.getItem(storageKey);
    return loadingState
      ? JSON.parse(loadingState)
      : { csv: false, txt: false, pdf: false };
  });

  const updateLoadingState = (newLoadingState: object) => {
    setLoading((prev: { csv: boolean; txt: boolean; pdf: boolean }) => {
      const updatedState: { csv: boolean; txt: boolean; pdf: boolean } = {
        ...prev,
        ...newLoadingState,
      };
      const storageKey = `${personNumber}_${remittenceCode}_loadingState`;
      localStorage.setItem(storageKey, JSON.stringify(updatedState));
      return updatedState;
    });
  };

  const getLoadingKey = (fileType: string): 'csv' | 'pdf' | 'txt' => {
    const fileTypeMap: Record<string, 'csv' | 'pdf' | 'txt'> = {
      '1': 'csv',
      '2': 'pdf',
      '3': 'txt',
    };
    return fileTypeMap[fileType];
  };

  const handleGenerateReportWithLoading = async (fileType: string) => {
    const loadingKey = getLoadingKey(fileType);

    updateLoadingState({ [loadingKey]: true });

    try {
      const response = await service.getCoparticipationGenerateReport({
        personNumber,
        remittenceCode,
        fileType,
      });

      return response;
    } catch (error) {
      if (
        error instanceof Error &&
        (error as AxiosError).response?.status === 400
      ) {
        return (error as AxiosError).response;
      }

      toastifyApiErrors(error, enqueueSnackbar);
      throw error;
    }
  };

  const hasRequestError = (failureReason: UseQueryResult['error']): boolean => {
    const error = failureReason as AxiosError;
    return error?.response?.status === 500;
  };

  const hasError = hasRequestError(
    coparticipationReportRequestState.failureReason,
  );

  const { csvFiles, pdfFiles, txtFiles } = reports;

  useEffect(() => {
    const storageKey = `${personNumber}_${remittenceCode}_loadingState`;
    const loadingState = localStorage.getItem(storageKey);
    setLoading(
      loadingState
        ? JSON.parse(loadingState)
        : { csv: false, txt: false, pdf: false },
    );
  }, [personNumber, remittenceCode]);

  const reportsToDownload =
    csvFiles.length > 0 && pdfFiles.length > 0 && txtFiles.length > 0;

  const showAlert = useMemo(() => {
    return loading.csv || loading.pdf || loading.txt;
  }, [loading.csv, loading.pdf, loading.txt]);

  return (
    <SummaryCardNew.Container marginTop={3}>
      <SummaryCardNew.Header title="Relatórios" />
      {coparticipationReportRequestState?.isLoading && (
        <Loading
          padding={8}
          message={
            <>
              Estamos carregando as informações. <br /> Em breve, tudo estará
              pronto para você
            </>
          }
        />
      )}
      {hasError && <ErrorOrNotFoundContent />}

      {!hasError && !coparticipationReportRequestState?.isLoading && (
        <>
          <Grid item container spacing={1}>
            <Grid item xs={isMobile ? 12 : 4}>
              <ReportButton disabled={!csvFiles}>
                <ReportButtonContent
                  label="CSV"
                  report={csvFiles}
                  onDownload={() => {
                    handleDownloadReport({
                      reportsFile: csvFiles,
                      personNumberFile: personNumber,
                    });
                  }}
                  onGenerateReport={() => handleGenerateReportWithLoading('1')}
                  loading={loading.csv}
                />
              </ReportButton>
            </Grid>
            <Grid item xs={isMobile ? 12 : 4}>
              <ReportButton disabled={!txtFiles}>
                <ReportButtonContent
                  label="TXT"
                  report={txtFiles}
                  onDownload={() => {
                    handleDownloadReport({
                      reportsFile: txtFiles,
                      personNumberFile: personNumber,
                    });
                  }}
                  onGenerateReport={() => handleGenerateReportWithLoading('3')}
                  loading={loading.txt}
                />
              </ReportButton>
            </Grid>
            <Grid item xs={isMobile ? 12 : 4}>
              <ReportButton disabled={!pdfFiles}>
                <ReportButtonContent
                  label="PDF"
                  report={pdfFiles}
                  onDownload={() => {
                    handleDownloadReport({
                      reportsFile: pdfFiles,
                      personNumberFile: personNumber,
                    });
                  }}
                  onGenerateReport={() => handleGenerateReportWithLoading('2')}
                  loading={loading.pdf}
                />
              </ReportButton>
            </Grid>
          </Grid>

          {showAlert && !reportsToDownload && (
            <AlertReport severity="info">
              <Typography variant="text" sx={{ whiteSpace: 'pre-line' }}>
                Prezado cliente, após a solicitação do relatório, o tempo médio
                para disponibilização dos arquivos para download é de até 24h.
              </Typography>
            </AlertReport>
          )}
        </>
      )}
    </SummaryCardNew.Container>
  );
}
